import React, { useState } from 'react';
import { X, ChevronDown, ChevronRight, Copy, Check, Loader2, AlertTriangle, Zap, Database, Bug, Wrench } from 'lucide-react';

const TYPE_META = {
  ENV:        { label: 'Environnement', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Wrench,        dot: 'bg-orange-400' },
  REGRESSION: { label: 'Régression',    color: 'bg-red-100 text-red-700 border-red-200',           icon: Bug,           dot: 'bg-red-500'    },
  DATA:       { label: 'Données',       color: 'bg-blue-100 text-blue-700 border-blue-200',         icon: Database,      dot: 'bg-blue-400'   },
  FLAKY:      { label: 'Instable',      color: 'bg-yellow-100 text-yellow-700 border-yellow-200',   icon: Zap,           dot: 'bg-yellow-400' },
};

const COMPLEXITY_META = {
  low:    { label: '< 30 min', color: 'bg-green-100 text-green-700' },
  medium: { label: '30 min – 2 h', color: 'bg-amber-100 text-amber-700' },
  high:   { label: '> 2 h', color: 'bg-red-100 text-red-700' },
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition"
      title="Copier"
    >
      {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copié !' : 'Copier'}
    </button>
  );
}

function ClusterCard({ cluster, index }) {
  const [expanded, setExpanded] = useState(index === 0);
  const [showTests, setShowTests] = useState(false);
  const [showTicket, setShowTicket] = useState(false);

  const meta = TYPE_META[cluster.type] || TYPE_META.REGRESSION;
  const TypeIcon = meta.icon;
  const complexMeta = COMPLEXITY_META[cluster.estimatedComplexity] || COMPLEXITY_META.medium;

  const ticketText = cluster.draftTicket
    ? `${cluster.draftTicket.title}\n\n${cluster.draftTicket.description}`
    : '';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      {/* Header cluster */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 transition"
      >
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
          {cluster.priority || index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${meta.color}`}>
              <TypeIcon className="w-3 h-3" />
              {meta.label}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${complexMeta.color}`}>
              {complexMeta.label}
            </span>
            <span className="text-xs text-gray-500">
              {cluster.affectedCount || cluster.affectedTests?.length || 0} test{(cluster.affectedCount || 0) > 1 ? 's' : ''}
            </span>
          </div>
          <div className="font-semibold text-gray-800 text-sm leading-snug pr-2">{cluster.title}</div>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3">
          {/* Root cause */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Analyse</div>
            <p className="text-sm text-gray-700 leading-relaxed">{cluster.rootCauseAnalysis}</p>
          </div>

          {/* Suggested fix */}
          {cluster.suggestedFix && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Action suggérée</div>
              <p className="text-sm text-gray-700 bg-indigo-50 rounded-lg px-3 py-2 border border-indigo-100 leading-relaxed">
                {cluster.suggestedFix}
              </p>
            </div>
          )}

          {/* Tests affectés */}
          {cluster.affectedTests?.length > 0 && (
            <div>
              <button
                onClick={() => setShowTests(s => !s)}
                className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700"
              >
                {showTests ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Tests affectés ({cluster.affectedTests.length})
              </button>
              {showTests && (
                <ul className="mt-2 space-y-1">
                  {cluster.affectedTests.map((name, i) => (
                    <li key={i} className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1 font-mono truncate" title={name}>
                      • {name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Draft ticket */}
          {cluster.draftTicket && (
            <div>
              <button
                onClick={() => setShowTicket(s => !s)}
                className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700"
              >
                {showTicket ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Draft ticket Azure DevOps
              </button>
              {showTicket && (
                <div className="mt-2 bg-gray-50 rounded-lg border border-gray-200 p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold text-gray-800">{cluster.draftTicket.title}</p>
                    <CopyButton text={ticketText} />
                  </div>
                  <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">{cluster.draftTicket.description}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Panneau latéral d'analyse IA d'une suite de tests.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   squadName: string
 *   failedCount: number
 *   isLoading: boolean
 *   error: string | null
 *   result: { executiveSummary, clusters } | null
 *   onAnalyze: () => void  — déclenche l'analyse
 */
export default function SuiteAnalysisPanel({ open, onClose, squadName, failedCount, isLoading, error, result, onAnalyze }) {
  return (
    <>
      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={onClose}
        />
      )}

      {/* Panneau */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 text-white flex-shrink-0">
          <div>
            <div className="font-bold text-base">Analyse IA — {squadName}</div>
            <div className="text-xs text-indigo-200 mt-0.5">
              {failedCount} test{failedCount > 1 ? 's' : ''} échoué{failedCount > 1 ? 's' : ''} à analyser
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Bouton analyser */}
          {!result && !isLoading && !error && (
            <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center">
                <Bug className="w-8 h-8 text-indigo-400" />
              </div>
              <div>
                <p className="font-semibold text-gray-800">Prêt à analyser</p>
                <p className="text-sm text-gray-500 mt-1">
                  L'IA va regrouper les erreurs, identifier les causes racines et proposer un ordre de traitement.
                </p>
              </div>
              <button
                onClick={onAnalyze}
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition shadow"
              >
                Lancer l'analyse
              </button>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-center space-y-3">
              <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
              <p className="font-semibold text-gray-700">Analyse en cours…</p>
              <p className="text-sm text-gray-400">Clustering des erreurs et identification des causes racines</p>
            </div>
          )}

          {/* Erreur */}
          {error && !isLoading && (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-3">
              <AlertTriangle className="w-10 h-10 text-red-400" />
              <p className="font-semibold text-gray-700">Erreur lors de l'analyse</p>
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2">{error}</p>
              <button
                onClick={onAnalyze}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition"
              >
                Réessayer
              </button>
            </div>
          )}

          {/* Résultats */}
          {result && !isLoading && (
            <>
              {/* Résumé exécutif */}
              {result.executiveSummary && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-sm font-semibold text-indigo-800">Résumé</p>
                    {result._fromCache && (
                      <span className="text-xs text-indigo-400">
                        cache · {result._cachedAt ? new Date(result._cachedAt).toLocaleString('fr-FR') : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-indigo-700 leading-relaxed">{result.executiveSummary}</p>
                </div>
              )}

              {/* Clusters */}
              {result.clusters?.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {result.clusters.length} cluster{result.clusters.length > 1 ? 's' : ''} identifié{result.clusters.length > 1 ? 's' : ''} — ordre de traitement recommandé
                  </p>
                  {result.clusters.map((cluster, i) => (
                    <ClusterCard key={cluster.id || i} cluster={cluster} index={i} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">Aucun cluster identifié.</p>
              )}

              {/* Relancer */}
              <button
                onClick={onAnalyze}
                className="w-full py-2 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50 transition"
              >
                Relancer l'analyse
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
